import * as THREE from 'three';
import type { PlanetConfig } from './types/planet';
import { SolarSystem } from './scene/SolarSystem';
import { HandTracker } from './hand/HandTracker';
import { PLANET_CONFIGS } from './data/planetConfigs';
import { GestureType } from './types/planet';
import { t, setLocale, getLocale } from './data/locale';

/**
 * 入口文件
 * 初始化太阳系场景，处理交互（鼠标 + 手势）
 */

// 全局引用
let solarSystem: SolarSystem;
let handTracker: HandTracker | null = null;

// DOM 元素
const loadingEl = document.getElementById('loading')!;
const loadingBarEl = document.getElementById('loading-bar')!;
const loadingTextEl = document.getElementById('loading-text')!;
const infoPanelEl = document.getElementById('info-panel')!;
const planetNameEl = document.getElementById('planet-name')!;
const planetInfoEl = document.getElementById('planet-info')!;
const gestureHintEl = document.getElementById('gesture-hint')!;
const langToggleBtn = document.getElementById('lang-toggle')! as HTMLButtonElement;
const gestureToggleBtn = document.getElementById('gesture-toggle')! as HTMLButtonElement;
const helpPanelEl = document.getElementById('help-panel')!;
const helpContentMouseEl = document.getElementById('help-content-mouse')!;
const helpContentGestureEl = document.getElementById('help-content-gesture')!;
const helpTabsEl = document.getElementById('help-tabs')!;
const gestureGuideEl = document.getElementById('gesture-guide')!;
const mouseGuideEl = document.getElementById('mouse-guide')!;
const gestureToastEl = document.getElementById('gesture-toast')!;
const gestureGuideBtn = document.getElementById('gesture-guide-btn')! as HTMLButtonElement;
const gestureGuideClose = document.getElementById('gesture-guide-close')! as HTMLButtonElement;
const infoCloseBtn = document.getElementById('info-close')! as HTMLButtonElement;
const timeControlEl = document.getElementById('time-control')!;
const timePlayBtn = document.getElementById('time-play')! as HTMLButtonElement;
const timeLabelEl = document.getElementById('time-label')!;
const timeSliderEl = document.getElementById('time-slider')! as HTMLInputElement;
const timeResetBtn = document.getElementById('time-reset')! as HTMLButtonElement;
const musicToggleBtn = document.getElementById('music-toggle')! as HTMLButtonElement;
const helpToggleBtn = document.getElementById('help-toggle')! as HTMLButtonElement;

// 音频相关
let audioContext: AudioContext | null = null;
let musicTimerId: ReturnType<typeof setTimeout> | null = null;
let reverbNode: ConvolverNode | null = null;
let masterGain: GainNode | null = null;
let musicEnabled = false;

// 手势状态
let gestureMode: boolean = false;
let currentPlanetName: string = '';  // 当前选中的行星名

/** 初始化 */
async function main() {
  const container = document.getElementById('app')!;

  // 创建太阳系
  solarSystem = new SolarSystem(container);

  // 注册加载进度回调
  solarSystem.setOnLoadProgress((loaded, total) => {
    const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
    loadingBarEl.style.width = `${percent}%`;
    loadingTextEl.textContent = `正在加载纹理... ${loaded}/${total} (${percent}%)`;

    // 所有纹理加载完成后隐藏加载界面
    if (loaded >= total) {
      setTimeout(() => {
        loadingEl.classList.add('hide');
        setTimeout(() => loadingEl.remove(), 500);
      }, 300);
    }
  });

  // 注册行星选取回调
  solarSystem.setOnPlanetSelect((name, config) => {
    currentPlanetName = name;
    showPlanetInfo(config);
  });

  // 注册行星取消选中回调（隐藏信息面板）
  solarSystem.setOnPlanetDeselect(() => {
    currentPlanetName = '';
    infoPanelEl.style.display = 'none';
  });

  // 设置鼠标/触摸交互
  setupMouseInteraction(container);

  // 设置键盘快捷键
  setupKeyboardControls();

  // 语言切换按钮
  setupLangToggle();

  // 手势开关按钮
  setupGestureToggle();

  // 时间控制面板
  setupTimeControl();

  // 音乐控制
  setupMusicControl();

  // 帮助面板 Tab 切换
  helpTabsEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.tab-btn') as HTMLButtonElement | null;
    if (!btn) return;
    const tab = btn.dataset.tab!;
    helpTabsEl.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    document.querySelectorAll('.help-tab-content').forEach(el => el.classList.remove('active'));
    const target = document.getElementById(`help-content-${tab}`);
    if (target) target.classList.add('active');
  });

  // 帮助按钮 — 打开帮助面板
  helpToggleBtn.addEventListener('click', () => {
    showHelp();
  });

  // 手势指南展开按钮
  gestureGuideBtn.addEventListener('click', () => {
    gestureGuideBtn.style.display = 'none';
    gestureGuideEl.style.display = 'block';
  });

  // 手势指南关闭按钮
  gestureGuideClose.addEventListener('click', () => {
    gestureGuideEl.style.display = 'none';
    gestureGuideBtn.style.display = 'block';
  });

  // 信息面板关闭按钮
  infoCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    infoPanelEl.style.display = 'none';
    // 同步取消跟踪
    if (currentPlanetName) {
      solarSystem.selectPlanet(currentPlanetName);
    }
  });

  // 点击 Three.js canvas 空白区域关闭信息面板
  const canvas = solarSystem.getRenderer().domElement;
  canvas.addEventListener('click', (e) => {
    infoPanelEl.style.display = 'none';
    if (currentPlanetName) {
      solarSystem.selectPlanet(currentPlanetName);
    }
  });

  // 开始动画循环
  solarSystem.animate();

  // 窗口大小变化
  window.addEventListener('resize', () => {
    solarSystem.onResize(window.innerWidth, window.innerHeight);
  });

  console.log('🌌 太阳系初始化完成！');
  updateHint();
}

/** 显示手势识别成功提示（短暂显示后自动消失） */
let _toastTimer: number | null = null;
function showGestureToast(text: string) {
  if (_toastTimer !== null) {
    clearTimeout(_toastTimer);
    _toastTimer = null;
  }
  gestureToastEl.textContent = text;
  gestureToastEl.classList.add('show');
  _toastTimer = window.setTimeout(() => {
    gestureToastEl.classList.remove('show');
    _toastTimer = null;
  }, 1800);
}

/** 显示持续悬浮提示（不会自动消失，直到手动隐藏） */
function showGestureToastPersist(text: string) {
  if (_toastTimer !== null) {
    clearTimeout(_toastTimer);
    _toastTimer = null;
  }
  gestureToastEl.textContent = text;
  gestureToastEl.classList.add('show');
}

/** 隐藏目前的悬浮提示 */
function hideGestureToast() {
  if (_toastTimer !== null) {
    clearTimeout(_toastTimer);
    _toastTimer = null;
  }
  gestureToastEl.classList.remove('show');
}

/** 设置手势开关按钮 */
function setupGestureToggle() {
  gestureToggleBtn.addEventListener('click', async () => {
    if (!gestureMode) {
      // 启用手势
      try {
        gestureHintEl.textContent = '🖐️ 正在启动摄像头...';
        showGestureToastPersist('🖐️ 正在启动摄像头，请稍候...');
        handTracker = new HandTracker(handleGesture);
        await handTracker.init();
        hideGestureToast();
        gestureMode = true;
        gestureToggleBtn.textContent = '🖐️ 手势: ON';
        gestureToggleBtn.classList.add('active');
        gestureHintEl.textContent = '✅ 手势控制已启用';
        gestureGuideEl.style.display = 'none';    // ★ 手势指南默认收起
        gestureGuideBtn.style.display = 'block';  // ★ 显示展开按钮
        mouseGuideEl.style.display = 'none';    // ★ 隐藏鼠标指南
        showGestureToast('🖐️ 手势控制已启用');
        updateHint();
      } catch (err) {
        hideGestureToast();
        const msg = (err as Error).message || String(err);
        // 更详细的错误分类
        if (msg.includes('HTTPS') || msg.includes('localhost') || msg.includes('摄像头不可用') || msg.includes('NotAllowed') || msg.includes('Permission') || msg.includes('NotFoundError')) {
          gestureHintEl.textContent = '❌ 摄像头不可用，请允许权限后重试';
          showGestureToast('❌ 请允许摄像头权限');
        } else if (msg.includes('iframe') || msg.includes('安全策略')) {
          gestureHintEl.textContent = '❌ 环境限制，请在浏览器中直接打开';
          showGestureToast('❌ 浏览器安全限制');
        } else {
          gestureHintEl.textContent = '❌ 启动失败：' + msg.substring(0, 30);
          showGestureToast('❌ 启动失败');
        }
        console.error('❌ 手势启动错误：', err);
        // ★ 不调用 updateHint()，保持错误信息可见
      }
    } else {
      // 关闭手势
      if (handTracker) {
        handTracker.stop();
        handTracker = null;
      }
      gestureMode = false;
      gestureToggleBtn.textContent = '🖐️ 手势: OFF';
      gestureToggleBtn.classList.remove('active');
      gestureHintEl.textContent = '🖱️ 已切换到鼠标模式';
      gestureGuideEl.style.display = 'none';
      gestureGuideBtn.style.display = 'none';
      mouseGuideEl.style.display = 'block';
      updateHint();
    }
  });
}

/** 处理手势事件 */
function handleGesture(event: any) {
  if (!solarSystem) return;

  switch (event.type) {
    case GestureType.OPEN_PALM: {
      // 五指张开 = 选取行星（最可靠手势 → 最重要操作）
      let planetName: string | null = null;
      if (event.handPosition) {
        const nx = event.handPosition.x * 2 - 1;
        const ny = -(event.handPosition.y * 2 - 1);
        planetName = getPlanetAtScreenPos(nx, ny);
      }
      if (!planetName) {
        planetName = getCenterPlanet();
      }
      if (planetName) {
        solarSystem.selectPlanet(planetName);
        const config = PLANET_CONFIGS.find(p => p.name === planetName);
        if (config) {
          gestureHintEl.textContent = '✋ ' + (getLocale() === 'zh' ? '已选取：' : 'Selected: ') + config.nameCn;
          showGestureToast('✋ ' + (getLocale() === 'zh' ? '已选取：' : 'Selected: ') + config.nameCn);
        }
      }
      break;
    }
    case GestureType.TWO_HAND_PINCH: {
      // 双手捏合 = 缩放
      const delta = event.data?.delta;
      if (delta) {
        const camera = solarSystem.getCamera();
        const factor = 1 + delta * 15;
        camera.position.multiplyScalar(factor);
        const toastText = delta > 0 ? ('🔍 缩小') : ('🔍 放大');
        gestureHintEl.textContent = delta > 0 ? t('zoomOut') : t('zoomIn');
        showGestureToast(toastText);
      }
      break;
    }
    case GestureType.SWIPE_LEFT: {
      const newSpeed = Math.max(0, solarSystem.getTimeScale() - 1);
      solarSystem.setTimeScale(newSpeed);
      updateTimePanelUI();
      gestureHintEl.textContent = t('speedDown');
      showGestureToast('👈 减速 → ' + newSpeed.toFixed(1) + 'x');
      break;
    }
    case GestureType.SWIPE_RIGHT: {
      const newSpeed = solarSystem.getTimeScale() + 1;
      solarSystem.setTimeScale(newSpeed);
      updateTimePanelUI();
      gestureHintEl.textContent = t('speedUp');
      showGestureToast('👉 加速 → ' + newSpeed.toFixed(1) + 'x');
      break;
    }

    case GestureType.SCISSORS: {
      // 剪刀手 → 重置视角
      solarSystem.resetView();
      gestureHintEl.textContent = t('resetView');
      showGestureToast('✌️ 视角已重置');
      break;
    }
    case GestureType.THUMB_UP: {
      const nextConfig = getNextPlanet();
      if (nextConfig) {
        solarSystem.selectPlanet(nextConfig.name);
        const label = getLocale() === 'zh' ? '下一个：' : 'Next: ';
        gestureHintEl.textContent = '👍 ' + label + nextConfig.nameCn;
        showGestureToast('👍 ' + label + nextConfig.nameCn);
      }
      break;
    }
    case GestureType.THUMB_DOWN: {
      const prevConfig = getPrevPlanet();
      if (prevConfig) {
        solarSystem.selectPlanet(prevConfig.name);
        const label = getLocale() === 'zh' ? '上一个：' : 'Prev: ';
        gestureHintEl.textContent = '👎 ' + label + prevConfig.nameCn;
        showGestureToast('👎 ' + label + prevConfig.nameCn);
      }
      break;
    }
  }
}

/**
 * 在指定屏幕坐标处射线检测行星
 * @param nx 归一化X坐标 [-1, 1]，默认0（屏幕中心）
 * @param ny 归一化Y坐标 [-1, 1]，默认0（屏幕中心）
 */
function getPlanetAtScreenPos(nx = 0, ny = 0): string | null {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2(nx, ny);
  raycaster.setFromCamera(mouse, solarSystem.getCamera());

  const planets = Array.from(solarSystem.getPlanets().values());
  const meshes: THREE.Mesh[] = [];
  planets.forEach(group => {
    group.children.forEach(child => {
      if (child instanceof THREE.Mesh && child.name.endsWith('_mesh')) {
        meshes.push(child);
      }
    });
  });

  const intersects = raycaster.intersectObjects(meshes);
  if (intersects.length > 0) {
    return intersects[0].object.parent?.name || null;
  }
  return null;
}

/** 兼容旧名：获取屏幕中央的行星 */
function getCenterPlanet(): string | null {
  return getPlanetAtScreenPos(0, 0);
}

/** 鼠标/触摸交互 */
function setupMouseInteraction(container: HTMLElement) {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  container.addEventListener('click', (event) => {
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(mouse, solarSystem.getCamera());
    const planets = Array.from(solarSystem.getPlanets().values());
    const meshes: THREE.Mesh[] = [];
    planets.forEach(group => {
      group.children.forEach(child => {
        if (child instanceof THREE.Mesh && child.name.endsWith('_mesh')) {
          meshes.push(child);
        }
      });
    });

    const intersects = raycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      event.stopPropagation(); // ★ 阻止冒泡到 canvas 再关闭面板
      const hitMesh = intersects[0].object;
      const planetName = hitMesh.parent?.name;
      if (planetName) {
        solarSystem.selectPlanet(planetName);
      }
    }
  });
}

/** 键盘控制 */
function setupKeyboardControls() {
  window.addEventListener('keydown', (event) => {
    switch (event.key) {
      case ' ': {
        event.preventDefault();
        solarSystem.togglePause();
        updateTimePanelUI();
        gestureHintEl.textContent = solarSystem.getTimeScale() === 0
          ? t('pause')
          : t('resume');
        break;
      }
      case '+':
      case '=': {
        solarSystem.setTimeScale(solarSystem.getTimeScale() + 0.5);
        updateTimePanelUI();
        gestureHintEl.textContent = t('speedUp');
        break;
      }
      case '-':
      case '_': {
        const newSpeed = Math.max(0, solarSystem.getTimeScale() - 0.5);
        solarSystem.setTimeScale(newSpeed);
        updateTimePanelUI();
        gestureHintEl.textContent = t('speedDown');
        break;
      }
      case 'r':
      case 'R': {
        solarSystem.resetView();
        gestureHintEl.textContent = t('resetView');
        break;
      }
      case 'g':
      case 'G': {
        // 点击手势开关按钮
        gestureToggleBtn.click();
        break;
      }
      case 'h':
      case 'H': {
        showHelp();
        break;
      }
      case '0': {
        solarSystem.setTimeScale(1);
        updateTimePanelUI();
        gestureHintEl.textContent = getLocale() === 'zh' ? '⏱️ 时间已重置 → 1x' : '⏱️ Time reset → 1x';
        break;
      }
      default: {
        const num = parseInt(event.key);
        if (num >= 1 && num <= 9) {
          const config = PLANET_CONFIGS[num];
          if (config) {
            solarSystem.selectPlanet(config.name);
          }
        }
      }
    }
  });
}

/** 语言切换 */
function setupLangToggle() {
  langToggleBtn.textContent = getLocale() === 'zh' ? 'EN' : '中文';
  langToggleBtn.addEventListener('click', () => {
    const next = getLocale() === 'zh' ? 'en' : 'zh';
    setLocale(next);
    langToggleBtn.textContent = next === 'zh' ? 'EN' : '中文';
    updateHint();
    // 刷新行星标签语言
    solarSystem.setLanguage(next === 'zh' ? 'zh' : 'en');
    // 刷新行星信息面板
    if (currentPlanetName && infoPanelEl.style.display === 'block') {
      const config = PLANET_CONFIGS.find(p => p.name === currentPlanetName);
      if (config) showPlanetInfo(config);
    }
    // 刷新帮助面板
    updateHelpContent();
    // 刷新底部快捷键面板
    updateMouseGuide();
  });
}

/** 更新时间面板 UI：标签、滑块、播放按钮、颜色 */
function updateTimePanelUI(): void {
  const speed = solarSystem.getTimeScale();
  timeLabelEl.textContent = speed.toFixed(1) + 'x';
  timeSliderEl.value = String(speed);
  timePlayBtn.textContent = speed === 0 ? '▶️' : '⏸️';
  // 速度颜色
  if (speed < 0) {
    timeLabelEl.style.color = '#ff4444';
    timeControlEl.style.borderColor = 'rgba(255,68,68,0.5)';
  } else if (speed === 0) {
    timeLabelEl.style.color = '#ffaa00';
    timeControlEl.style.borderColor = 'rgba(255,170,0,0.5)';
  } else {
    timeLabelEl.style.color = '#aaa';
    timeControlEl.style.borderColor = 'rgba(255,255,255,0.15)';
  }
}

/** 时间控制面板 */
function setupTimeControl(): void {
  let prevSpeed = 1;

  // 播放/暂停
  timePlayBtn.addEventListener('click', () => {
    const speed = solarSystem.getTimeScale();
    if (speed === 0) {
      solarSystem.setTimeScale(prevSpeed);
    } else {
      prevSpeed = speed;
      solarSystem.setTimeScale(0);
    }
    updateTimePanelUI();
  });

  // 速度滑块
  timeSliderEl.addEventListener('input', () => {
    const speed = parseFloat(timeSliderEl.value);
    solarSystem.setTimeScale(speed);
    updateTimePanelUI();
  });

  // 重置按钮
  timeResetBtn.addEventListener('click', () => {
    solarSystem.setTimeScale(1);
    updateTimePanelUI();
  });

  updateTimePanelUI();
}

/** 设置音乐控制 */
/**
 * 播放一个"钢琴音" — 使用多层谐波模拟柔和的钢琴音色
 * @param freq 基频
 * @param startTime 开始时间
 * @param duration 持续时间（秒）
 * @param velocity 力度 0-1
 */
function playPianoNote(
  freq: number,
  startTime: number,
  duration: number,
  velocity: number = 0.5
): void {
  if (!audioContext || !masterGain || !reverbNode) return;

  // 干净的直达声通道
  const dryGain = audioContext.createGain();
  dryGain.connect(masterGain);

  // 混响声通道
  const wetGain = audioContext.createGain();
  wetGain.gain.value = velocity * 0.3; // 混响比例
  wetGain.connect(reverbNode);

  // 钢琴谐波结构：基频 + 2倍 + 3倍 + 4倍 + 5倍（递减）
  const harmonics = [
    { mult: 1, amp: 1.0 },     // 基频
    { mult: 2, amp: 0.5 },     // 八度
    { mult: 3, amp: 0.2 },     // 五度+八度
    { mult: 4, amp: 0.1 },     // 两个八度
    { mult: 5, amp: 0.05 },    // 三度
  ];

  harmonics.forEach(({ mult, amp }) => {
    const osc = audioContext!.createOscillator();
    const oscGain = audioContext!.createGain();

    osc.type = 'sine';
    osc.frequency.value = freq * mult;

    // ADSR 包络 — 模拟钢琴音色
    const a = 0.01;   // Attack: 快
    const d = 0.4;    // Decay: 中速衰减
    const s = 0.15;   // Sustain: 衰减后保持
    const r = 0.8;    // Release: 缓慢消失

    oscGain.gain.setValueAtTime(0, startTime);
    oscGain.gain.linearRampToValueAtTime(velocity * amp * 0.6, startTime + a);
    oscGain.gain.linearRampToValueAtTime(velocity * amp * s, startTime + a + d);
    oscGain.gain.setValueAtTime(velocity * amp * s, startTime + duration - r);
    oscGain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.connect(oscGain);
    oscGain.connect(dryGain);
    oscGain.connect(wetGain);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.1);
  });
}

// 实际频率（仅保留使用的）
const E2 = 82.41;
const E3 = 164.81;
const B2 = 123.47;
const B3 = 246.94;
const C4 = 261.63;
const D4 = 293.66;
const E4 = 329.63;
const G4 = 392;
const A4 = 440;

// One Day 风格旋律 — 以 E 小调为基础，极简主义，带大量留白
// 速度：约 60 BPM，每拍 1 秒，每个音符持续 3-4 秒
interface NoteEvent {
  freq: number;
  time: number; // 从乐句开始起的偏移（秒）
  duration: number;
  velocity: number;
}

// Phrase A: E4 - G4 - A4 - G4 - E4
function phraseA(startTime: number, vel: number = 0.6): NoteEvent[] {
  const beat = 1.0; // 60 BPM
  return [
    { freq: E4, time: startTime + 0 * beat, duration: 3.5, velocity: vel },
    { freq: G4, time: startTime + 1 * beat, duration: 3.5, velocity: vel },
    { freq: A4, time: startTime + 2 * beat, duration: 3.5, velocity: vel },
    { freq: G4, time: startTime + 3 * beat, duration: 3.5, velocity: vel },
    { freq: E4, time: startTime + 4 * beat, duration: 3.5, velocity: vel },
  ];
}

// Phrase B: D4 - C4 - B3 - C4 - D4 - E4
function phraseB(startTime: number, vel: number = 0.6): NoteEvent[] {
  const beat = 1.0;
  return [
    { freq: D4, time: startTime + 0 * beat, duration: 3.5, velocity: vel },
    { freq: C4, time: startTime + 1 * beat, duration: 3.5, velocity: vel },
    { freq: B3, time: startTime + 2 * beat, duration: 3.5, velocity: vel },
    { freq: C4, time: startTime + 3 * beat, duration: 3.5, velocity: vel },
    { freq: D4, time: startTime + 4 * beat, duration: 3.5, velocity: vel },
    { freq: E4, time: startTime + 5 * beat, duration: 3.5, velocity: vel },
  ];
}

// Phrase C: E4 - D4 - C4 - B3 - C4 - D4 - E4 (下行+上行)
function phraseC(startTime: number, vel: number = 0.6): NoteEvent[] {
  const beat = 1.0;
  return [
    { freq: E4, time: startTime + 0 * beat, duration: 3.0, velocity: vel },
    { freq: D4, time: startTime + 0.5 * beat, duration: 3.0, velocity: vel * 0.8 },
    { freq: C4, time: startTime + 1 * beat, duration: 3.0, velocity: vel },
    { freq: B3, time: startTime + 1.5 * beat, duration: 3.0, velocity: vel * 0.8 },
    { freq: C4, time: startTime + 2 * beat, duration: 3.0, velocity: vel },
    { freq: D4, time: startTime + 2.5 * beat, duration: 3.0, velocity: vel * 0.8 },
    { freq: E4, time: startTime + 3 * beat, duration: 3.0, velocity: vel },
  ];
}

// 和声铺底（持续低音，营造太空氛围）
function harmonyPad(startTime: number, dur: number, vel: number = 0.35): NoteEvent[] {
  return [
    { freq: E2, time: startTime, duration: dur, velocity: vel },
    { freq: B2, time: startTime, duration: dur, velocity: vel * 0.7 },
    { freq: E3, time: startTime, duration: dur, velocity: vel * 0.6 },
  ];
}

// 完整的旋律编排（循环）
function buildMelodyLoop(startTime: number): NoteEvent[] {
  let t = startTime;
  const notes: NoteEvent[] = [];

  // 小节 1: Phrase A + 和声
  notes.push(...phraseA(t, 0.65));
  notes.push(...harmonyPad(t, 6, 0.3));
  t += 6;

  // 小节 2: Phrase B + 和声
  notes.push(...phraseB(t, 0.65));
  notes.push(...harmonyPad(t, 7, 0.3));
  t += 7;

  // 小节 3: Phrase A + 和声（重复，营造循环感）
  notes.push(...phraseA(t, 0.65));
  notes.push(...harmonyPad(t, 6, 0.3));
  t += 6;

  // 小节 4: Phrase C（变化）+ 和声
  notes.push(...phraseC(t, 0.65));
  notes.push(...harmonyPad(t, 5, 0.3));
  t += 5;

  return notes;
}

// 低音铺底（持续低音嗡鸣，One Day 也有持续的低音铺底）
function buildDrone(startTime: number, duration: number): NoteEvent[] {
  return [
    { freq: 55,  time: startTime, duration, velocity: 0.08 }, // A1
    { freq: 82.4, time: startTime, duration, velocity: 0.06 }, // E2
  ];
}

function setupMusicControl(): void {
  async function createBackgroundMusic(): Promise<void> {
    if (audioContext) return;

    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    console.log('[Music] AudioContext:', audioContext.state, 'sampleRate:', audioContext.sampleRate);

    // ---- 混响（长混响，营造太空感） ----
    reverbNode = audioContext.createConvolver();
    const rate = audioContext.sampleRate;
    const length = rate * 4.0; // 4 秒长混响
    const impulse = audioContext.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
      }
    }
    reverbNode.buffer = impulse;

    // ---- 主音量 ----
    masterGain = audioContext.createGain();
    masterGain.gain.value = 0.8;
    masterGain.connect(audioContext.destination);

    // 混响连到输出
    reverbNode.connect(masterGain);

    // ---- 低音铺底（持续整个播放） ----
    const now = audioContext.currentTime;
    const droneNotes = buildDrone(now, 300); // 5 分钟铺底
    droneNotes.forEach(({ freq, time, duration, velocity }) => {
      const osc = audioContext!.createOscillator();
      const g = audioContext!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.value = 0; // 淡入
      osc.connect(g);
      g.connect(masterGain!);
      osc.start(time);
      g.gain.linearRampToValueAtTime(velocity, time + 2.0);
      g.gain.setValueAtTime(velocity, time + duration - 1);
      g.gain.linearRampToValueAtTime(0, time + duration + 0.5);
      osc.stop(time + duration + 1);
    });

    // ---- 旋律循环（One Day 风格） ----
    const loopDuration = 24; // 一个完整循环 24 秒

    function scheduleLoop() {
      if (!audioContext || !masterGain) return;
      const now = audioContext.currentTime;
      const melodyNotes = buildMelodyLoop(now);
      melodyNotes.forEach(({ freq, time, duration, velocity }) => {
        playPianoNote(freq, time, duration, velocity);
      });
      // 安排下一个循环
      musicTimerId = setTimeout(scheduleLoop, loopDuration * 1000);
    }

    scheduleLoop();
    console.log('[Music] One Day style melody started');
  }

  function stopBackgroundMusic(): void {
    if (musicTimerId) {
      clearTimeout(musicTimerId);
      musicTimerId = null;
    }
    if (audioContext && masterGain) {
      const now = audioContext.currentTime;
      masterGain.gain.cancelScheduledValues(now);
      masterGain.gain.setValueAtTime(masterGain.gain.value, now);
      masterGain.gain.linearRampToValueAtTime(0, now + 1.0);
      setTimeout(() => {
        if (audioContext) {
          audioContext.close();
          audioContext = null;
        }
        masterGain = null;
        reverbNode = null;
      }, 1200);
    }
  }

  musicToggleBtn.addEventListener('click', async () => {
    if (musicEnabled) {
      stopBackgroundMusic();
      musicToggleBtn.textContent = '🎵 音乐: OFF';
      musicToggleBtn.classList.remove('active');
      musicEnabled = false;
    } else {
      try {
        await createBackgroundMusic();
        musicToggleBtn.textContent = '🎵 音乐: ON';
        musicToggleBtn.classList.add('active');
        musicEnabled = true;
      } catch (err) {
        console.error('[Music] Failed:', err);
      }
    }
  });
}

/** 更新底部提示文字（根据当前模式显示对应操作说明） */
function updateHint() {
  const isZh = getLocale() === 'zh';
  if (gestureMode) {
    gestureHintEl.innerHTML = isZh
      ? '🖐️ <b>手势模式</b> | ✋ 五指张开选取 → ✌️ 剪刀手重置视角 | 按 <kbd style="background:rgba(255,255,255,0.15);padding:0 4px;border-radius:3px;font-size:11px;">H</kbd> 查看完整指南'
      : '🖐️ <b>Gesture Mode</b> | ✋ Open palm to select → ✌️ Scissors to reset view | Press <kbd style="background:rgba(255,255,255,0.15);padding:0 4px;border-radius:3px;font-size:11px;">H</kbd> for full guide';
  } else {
    gestureHintEl.innerHTML = isZh
      ? '🖱️ <b>鼠标模式</b> | 拖拽旋转 → 滚轮缩放 → 点击选取 | 按 <kbd style="background:rgba(255,255,255,0.15);padding:0 4px;border-radius:3px;font-size:11px;">G</kbd> 启用手势 | <kbd style="background:rgba(255,255,255,0.15);padding:0 4px;border-radius:3px;font-size:11px;">H</kbd> 帮助'
      : '🖱️ <b>Mouse Mode</b> | Drag to rotate → Scroll to zoom → Click to select | Press <kbd style="background:rgba(255,255,255,0.15);padding:0 4px;border-radius:3px;font-size:11px;">G</kbd> for gesture | <kbd style="background:rgba(255,255,255,0.15);padding:0 4px;border-radius:3px;font-size:11px;">H</kbd> help';
  }
}

/** 显示行星信息 */
function showPlanetInfo(config: PlanetConfig) {
  const nameStr = getLocale() === 'zh' ? `${config.nameCn} (${config.name})` : config.name;
  planetNameEl.textContent = nameStr;

  planetInfoEl.innerHTML = t('planetInfo', {
    name: nameStr,
    radius: config.realRadius.toLocaleString(),
    orbitAU: String(config.orbitRadius),
    period: String(config.yearPeriod),
    rotation: String(Math.abs(config.rotationPeriod)),
    mass: config.mass !== undefined ? String(config.mass) : '?',
    gravity: config.gravity !== undefined ? String(config.gravity) : '?',
    temp: config.temperature !== undefined ? String(config.temperature) : '?',
    moons: config.moons !== undefined ? String(config.moons) : '?',
    desc: config.info,
  }).replace(/\n/g, '<br>');

  infoPanelEl.style.display = 'block';
}

/** 显示帮助面板 */
function showHelp() {
  // 根据当前模式自动切换到对应 Tab
  const tab = gestureMode ? 'gesture' : 'mouse';
  helpTabsEl.querySelectorAll('.tab-btn').forEach(b => {
    b.classList.toggle('active', (b as HTMLElement).dataset.tab === tab);
  });
  document.querySelectorAll('.help-tab-content').forEach(el => el.classList.remove('active'));
  const target = document.getElementById(`help-content-${tab}`);
  if (target) target.classList.add('active');

  updateHelpContent();
  helpPanelEl.style.display = 'block';
}

/** 更新帮助面板内容（同时填充鼠标和手势两个 Tab） */
function updateHelpContent() {
  const isZh = getLocale() === 'zh';

  // ========== 鼠标/键盘 Tab ==========
  helpContentMouseEl.innerHTML = `
    <p class="help-section-title">🖱️ ${isZh ? '鼠标 / 触摸' : 'Mouse / Touch'}</p>
    <p>${isZh ? '拖拽空白区域' : 'Drag on empty area'} → ${isZh ? '旋转视角' : 'Rotate view'}</p>
    <p>${isZh ? '滚轮 / 双指' : 'Scroll / Pinch'} → ${isZh ? '缩放' : 'Zoom'}</p>
    <p>${isZh ? '点击行星' : 'Click a planet'} → ${isZh ? '选中并查看信息' : 'Select &amp; show info'}</p>
    <p>${isZh ? '再次点击已选行星' : 'Click selected planet again'} → ${isZh ? '取消跟踪' : 'Cancel follow'}</p>

    <p class="help-section-title">⌨️ ${isZh ? '键盘快捷键' : 'Keyboard Shortcuts'}</p>
    <p><kbd>1</kbd>–<kbd>9</kbd> → ${isZh ? '聚焦对应行星（1=水星…9=冥王星）' : 'Focus planet (1=Mercury…9=Pluto)'}</p>
    <p><kbd>Space</kbd> → ${isZh ? '暂停 / 继续运行' : 'Pause / Resume'}</p>
    <p><kbd>+</kbd> / <kbd>-</kbd> → ${isZh ? '时间加速 / 减速' : 'Speed up / Slow down'}</p>
    <p><kbd>R</kbd> → ${isZh ? '重置视角' : 'Reset view'}</p>
    <p><kbd>G</kbd> → ${isZh ? '开启 / 关闭手势控制' : 'Toggle gesture control'}</p>
    <p><kbd>H</kbd> → ${isZh ? '显示 / 隐藏此帮助' : 'Show / Hide this help'}</p>

    <p class="help-section-title">🕐 ${isZh ? '时间控制面板' : 'Time Control Panel'}</p>
    <p>${isZh ? '底部控制条' : 'Bottom control bar'} → ${isZh ? '播放/暂停、速度滑块、重置按钮' : 'Play/Pause, speed slider, reset'}</p>

    <p class="help-section-title">🌐 ${isZh ? '其他' : 'Other'}</p>
    <p>EN / 中文 → ${isZh ? '右上角切换语言' : 'Top-right language switch'}</p>
    <p>🎵 ${isZh ? '音乐按钮 → 开关太空背景音' : 'Music button → Toggle space ambience'}</p>
  `;

  // ========== 手势操作 Tab ==========
  helpContentGestureEl.innerHTML = `
    <p style="color:#aaa;font-size:13px;margin-bottom:12px;">${isZh ? '👆 先按 <kbd>G</kbd> 或点击右上角「🖐️ 手势」按钮启用手势控制，然后对着摄像头做以下手势：' : '👆 Press <kbd>G</kbd> or click the top-right "🖐️ Gesture" button to enable hand tracking, then make these gestures:'}</p>

    <p class="help-section-title">🎯 ${isZh ? '选取与导航' : 'Select &amp; Navigate'}</p>
    <div class="help-gesture-grid">
      <span class="g-icon">✋</span><span class="g-desc">${isZh ? '五指张开 → 选取行星（可指向不同星球）' : 'Open palm → Select planet (point anywhere)'}</span>
      <span class="g-icon">👍</span><span class="g-desc">${isZh ? '大拇指朝上 → 下一个行星' : 'Thumb up → Next planet'}</span>
      <span class="g-icon">👎</span><span class="g-desc">${isZh ? '大拇指朝下 → 上一个行星' : 'Thumb down → Prev planet'}</span>
    </div>

    <p class="help-section-title">🔍 ${isZh ? '视角控制' : 'View Control'}</p>
    <div class="help-gesture-grid">
      <span class="g-icon">🤚</span><span class="g-desc">${isZh ? '双手捏合/张开 → 缩放视角' : 'Two-hand pinch/spread → Zoom in/out'}</span>
      <span class="g-icon">✌️</span><span class="g-desc">${isZh ? '剪刀手（食指+中指V形）→ 重置视角' : 'Scissors (V sign) → Reset view'}</span>
    </div>

    <p class="help-section-title">⏱️ ${isZh ? '时间控制' : 'Time Control'}</p>
    <div class="help-gesture-grid">
            <span class="g-icon">👈</span><span class="g-desc">${isZh ? '食指指向左 → 时间减速' : 'Point left → Slow down'}</span>
      <span class="g-icon">👉</span><span class="g-desc">${isZh ? '食指指向右 → 时间加速' : 'Point right → Speed up'}</span>
    </div>

    <p style="margin-top:14px;color:#f0c040;font-size:12px;">💡 ${isZh ? '提示：底部有手势操作提示，识别成功会有Toast通知。' : 'Tip: Gesture hints at bottom; toasts on success.'}</p>
  `;
}

/** 更新右侧快捷键面板和手势指南面板 */
function updateMouseGuide() {
  const isZh = getLocale() === 'zh';
  // 鼠标快捷键面板
  mouseGuideEl.innerHTML = `
    <h3>${isZh ? '🖱️ 鼠标快捷键' : '🖱️ Mouse Shortcuts'}</h3>
    <div class="key-item"><span class="key-icon">🖱️ ${isZh ? '拖拽' : 'Drag'}</span><span class="key-desc">${isZh ? '旋转视角' : 'Rotate view'}</span></div>
    <div class="key-item"><span class="key-icon">🖱️ ${isZh ? '滚轮' : 'Scroll'}</span><span class="key-desc">${isZh ? '缩放' : 'Zoom'}</span></div>
    <div class="key-item"><span class="key-icon">🖱️ ${isZh ? '点击' : 'Click'}</span><span class="key-desc">${isZh ? '选取行星' : 'Select planet'}</span></div>
    <hr style="border-color:rgba(255,255,255,0.1);margin:8px 0;">
    <div class="key-item"><span class="key-icon">Space</span><span class="key-desc">${isZh ? '暂停/继续' : 'Pause/Resume'}</span></div>
    <div class="key-item"><span class="key-icon">+ / -</span><span class="key-desc">${isZh ? '时间加速/减速' : 'Speed up/down'}</span></div>
    <div class="key-item"><span class="key-icon">1 - 9</span><span class="key-desc">${isZh ? '聚焦对应行星' : 'Focus planet'}</span></div>
    <div class="key-item"><span class="key-icon">R</span><span class="key-desc">${isZh ? '重置视角' : 'Reset view'}</span></div>
    <div class="key-item"><span class="key-icon">G</span><span class="key-desc">${isZh ? '开启手势控制' : 'Toggle gesture'}</span></div>
    <div class="key-item"><span class="key-icon">H</span><span class="key-desc">${isZh ? '帮助面板' : 'Help panel'}</span></div>
    <div class="key-item"><span class="key-icon">0</span><span class="key-desc">${isZh ? '重置时间速度' : 'Reset time'}</span></div>
  `;
  // 手势指南面板（右下角）
  gestureGuideEl.innerHTML = `
    <h3>${isZh ? '🖐️ 手势操作指南' : '🖐️ Gesture Guide'}</h3>
    <div class="gesture-item"><span class="gesture-icon">✋</span><span class="gesture-desc">${isZh ? '五指张开 → 选取行星' : 'Open palm → Select planet'}</span></div>
    <div class="gesture-item"><span class="gesture-icon">👍</span><span class="gesture-desc">${isZh ? '大拇指朝上 → 下一个行星' : 'Thumb up → Next planet'}</span></div>
    <div class="gesture-item"><span class="gesture-icon">👎</span><span class="gesture-desc">${isZh ? '大拇指朝下 → 上一个行星' : 'Thumb down → Prev planet'}</span></div>
    <hr style="border-color:rgba(255,255,255,0.1);margin:8px 0;">
    <div class="gesture-item"><span class="gesture-icon">🤚</span><span class="gesture-desc">${isZh ? '双手靠近/远离 → 缩放' : 'Two hands closer/farther → Zoom'}</span></div>
    <div class="gesture-item"><span class="gesture-icon">✌️</span><span class="gesture-desc">${isZh ? '剪刀手 → 重置视角' : 'Scissors → Reset view'}</span></div>
    <hr style="border-color:rgba(255,255,255,0.1);margin:8px 0;">
    <div class="gesture-item"><span class="gesture-icon">👈</span><span class="gesture-desc">${isZh ? '食指指向左 → 时间减速' : 'Point left → Slow down'}</span></div>
    <div class="gesture-item"><span class="gesture-icon">👉</span><span class="gesture-desc">${isZh ? '食指指向右 → 时间加速' : 'Point right → Speed up'}</span></div>
  `;
}

/** 获取下一个行星配置（按数组顺序循环） */
function getNextPlanet(): PlanetConfig | null {
  if (PLANET_CONFIGS.length === 0) return null;
  const idx = PLANET_CONFIGS.findIndex(c => c.name === currentPlanetName);
  const nextIdx = idx < 0 ? 0 : (idx + 1) % PLANET_CONFIGS.length;
  return PLANET_CONFIGS[nextIdx];
}

/** 获取上一个行星配置（按数组顺序循环） */
function getPrevPlanet(): PlanetConfig | null {
  if (PLANET_CONFIGS.length === 0) return null;
  const idx = PLANET_CONFIGS.findIndex(c => c.name === currentPlanetName);
  const prevIdx = idx < 0 ? PLANET_CONFIGS.length - 1 : (idx - 1 + PLANET_CONFIGS.length) % PLANET_CONFIGS.length;
  return PLANET_CONFIGS[prevIdx];
}

// 启动
main().catch(console.error);

/** 获取上一个行星配置（按数组顺序循环） */
